import { styled, alpha } from "@mui/material/styles";
import { Box, Typography} from "@mui/material";

export const SidebarContainer = styled("div")`
  display: flex;
  position: fixed;
  top: 48px;
  left: 0;
  bottom: 0;
  z-index: 1200;
  transition: all 0.3s ease;
  height: calc(100vh - 48px);
`;

export const PrimarySidebar = styled("div")(({ theme }) => ({
  width: "45px",
  height: "100%",
  backgroundColor: theme.sidebar.primary,
  display: "flex",
  flexDirection: "column",
  padding: "16px 0",
  borderRight: `1px solid ${theme.sidebar.text.primary}`,
}));

export const SecondarySidebar = styled("div")(({ theme, collapsed }) => ({
  width: collapsed ? 0 : "180px",
  height: "100%",
  backgroundColor: theme.sidebar.secondary,
  opacity: collapsed ? 0 : 1,
  transition: "all 0.4s ease",
}));

export const ScrollableContent = styled("div")({
  height: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  padding: "1px 6px",

  "&::-webkit-scrollbar": {
    width: "2px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha("#FFFFFF", 0.1),
    borderRadius: "2px",
    "&:hover": {
      background: alpha("#FFFFFF", 0.15),
    },
  },
});
export const MenuSection = styled(Box)({
  "& + &": {
    marginTop: "8px",
  },

  "&:last-child": {
    paddingBottom: "16px",
  }
});

export const MenuSubSection = styled(Box)({
  "& + &": {
    marginTop: "8px",
  },

  "&:last-child": {
    paddingBottom: "36px",
      marginLeft:"8px"
  }
});

export const PrimaryMenuItem = styled(Box)(({ theme, active }) => ({
  width: "32px",
  height: "32px",
  display: "flex",
  borderRadius: "8px",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  backgroundColor: active ? theme.sidebar.active : "transparent",
  color: active ? theme.sidebar.text.primary : theme.sidebar.text.secondary,
  margin: "0 auto",
  
  "& .MuiSvgIcon-root": {
    fontSize: "20px",
  },
  "&:hover": {
    backgroundColor: theme.sidebar.hover,
    color: theme.sidebar.text.hover,
  },
}));

export const MenuItem = styled(Box)(({ theme, active }) => {
  const colors = theme.palette.mode === 'dark' ? theme.sidebar : theme.sidebar;
  
  return {
    padding: "6px 12px",
    margin: "2px 0",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: active ? colors.active : "transparent",
    color: active ? colors.text.primary : colors.text.secondary,
    height: "36px",
    transition: "all 0.2s ease",

    "& .MuiSvgIcon-root": {
      fontSize: "18px",
      transition: "all 0.2s ease",
    },

    "& span": {
      fontSize: "12px",
      fontWeight: 500,
      letterSpacing: "0.1px",
      transition: "all 0.2s ease",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    "&:hover": {
      backgroundColor: colors.hover,
      color: colors.text.hover,
      transform: "translateX(2px)",
    },
  };
});



export const MenuBadge = styled(Box)(({ theme }) => ({
  padding: "1px 6px",
  borderRadius: "3px",
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.default, 0.4)
      : alpha(theme.palette.primary.main, 0.08),
  color:
    theme.palette.mode === "dark"
      ? theme.palette.primary.light
      : theme.palette.primary.light,
  fontSize: "10px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  height: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "28px",
}));

export const CollapseButton = styled("button")(({ theme, collapsed }) => ({
  width: "32px",                    
  height: "32px",
  border: "none",
  backgroundColor: theme.sidebar.active,
  borderRadius: "10px",           
  display: "flex",              
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  padding: 0,
  color: theme.sidebar.text.secondary,
  transition: "all 0.2s ease",     
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",  

  "& .MuiSvgIcon-root": {
    width: 18,                   
    height: 18,
    transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
    transition: "all 0.3s ease",  
  },

  "&:hover": {
    color: theme.sidebar.text.primary,
    backgroundColor: theme.sidebar.hover,
    transform: "translateY(-1px)",  
    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",  
  },

  "&:active": {
    transform: "translateY(0)",    
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  }
}));

export const ProfileSection = styled(Box)(({ theme, collapsed }) => ({
  marginTop: "auto",
  padding: "12px",
  borderTop: `1px solid ${theme.sidebar.divider}`,
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minHeight: "68px",
  backgroundColor: theme.sidebar.primary,
  position: "sticky",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1,
  
  "& .MuiAvatar-root": {
    width: "32px",
    height: "32px",
  }
}));

export const ProfileAvatar = styled("div")(({ theme }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  overflow: "hidden",
  flexShrink: 0,
  border: `1px solid ${theme.palette.divider}`,

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));



export const MenuTitle = styled(Typography)(({ theme, collapsed }) => {
  const colors = theme.palette.mode === 'dark' ? theme.sidebar : theme.sidebar;
  
  return {
    color: colors.text.secondary,
    fontSize: "10px",
    fontWeight: 500,
    textTransform: "uppercase",
    padding: "0 12px",
    marginBottom: "4px",
    marginTop: "20px",
    display: collapsed ? "none" : "block",
    letterSpacing: "0.5px",
    opacity: 0.9,
  };
});



export const ProfileCard = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: '92px',
  left: '52px',
  backgroundColor: '#3B4374',
  borderRadius: '8px',
  padding: '12px 14px',
  minWidth: '180px',       
  maxWidth: '240px',        
  width: 'fit-content',
  border: '1px solid #4C5488',
  display: 'none',   
  boxShadow: '0 8px 24px rgba(0,0,0,0.28)',  
  
  '& p, & span': {  
    overflow: 'visible',
    textOverflow: 'clip',         
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },

  '&.show': {
    display: 'block',
    animation: 'fadeIn 0.2s ease-out',
  },
  zIndex: 1300,
  '&:before': {
    content: '""',
    position: 'absolute',
    left: '-4px',
    top: '50%',
    transform: 'translateY(-50%) rotate(45deg)',
    width: '8px',
    height: '8px',
    backgroundColor: '#3B4374',
    border: '1px solid #4C5488',
    borderRight: 'none',
    borderTop: 'none',
  }
}));



export const ProfileCardContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

export const ProfileName = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#FFFFFF',
  letterSpacing: '0.3px',
  lineHeight: 1.2,
}));

export const ProfileSectionName = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#C6C8F1',
  letterSpacing: '0.3px',
  lineHeight: 1.2,
  marginTop:"3px"
}));


export const ProfileCollapseSection = styled(Typography)(({ theme }) => ({
  padding:"10px 16px"
}));

export const ProfileRole = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: '#B8BCE3',
  letterSpacing: '0.2px',
  lineHeight: 1.2,
}));

